import { useEffect, useState } from 'react'
import { adminService } from '../../services/admin.service'
import AppFeatureTogglesCard from '../../components/admin/AppFeatureTogglesCard'
import MapActionsEditor from '../../components/admin/map-actions/MapActionsEditor'
import MapFiltersTable from '../../components/admin/MapFiltersTable'
import SpotlightCurrentVenueSettings from '../../components/admin/SpotlightCurrentVenueSettings'
import SpotlightEligibilityEditor from '../../components/admin/SpotlightEligibilityEditor'
import SpotlightBadgePriorityTable from '../../components/admin/SpotlightBadgePriorityTable'
import VenueTypeLabelsTable from '../../components/admin/VenueTypeLabelsTable'
import SpotlightSortOrderTable from '../../components/admin/SpotlightSortOrderTable'
import SpotlightTickerGuide from '../../components/admin/SpotlightTickerGuide'
import SpotlightTrendingSettings from '../../components/admin/SpotlightTrendingSettings'
import VenueAggregateWindowsEditor from '../../components/admin/VenueAggregateWindowsEditor'
import TextInput from '../../components/ui/TextInput'
import {
  buildDefaultMapFilterConfig,
  DEFAULT_FEEDBACK_AUTO_REPLY_MESSAGE,
  DEFAULT_SPOTLIGHT_VENUE_COUNT,
  DEFAULT_SUPPORT_NAME,
  DEFAULT_VENUE_SUGGESTION_AUTO_REPLY_MESSAGE,
  FEEDBACK_AUTO_REPLY_MAX_LENGTH,
  VENUE_SUGGESTION_AUTO_REPLY_MAX_LENGTH,
  FeatureFlagKey,
  FeatureListItemDto,
  isValidSemverXyz,
  MapActionsConfig,
  MapFilterConfigItemDto,
  MapVersion,
  MAX_SPOTLIGHT_VENUE_COUNT,
  MAX_SPOTLIGHT_VENUE_CANDIDATES,
  MIN_SPOTLIGHT_VENUE_COUNT,
  hasEnabledSpotlightEligibilityRule,
  MAX_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS,
  MAX_SPOTLIGHT_SEARCH_RADIUS_DEGREES,
  MIN_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS,
  MIN_SPOTLIGHT_SEARCH_RADIUS_DEGREES,
  normalizeMapFilterConfig,
  parseMapActions,
  parseSpotlightBadgePriority,
  parseSpotlightCurrentVenue,
  parseSpotlightEligibility,
  parseSpotlightSortOrder,
  parseSpotlightTrending,
  parseSpotlightVenueCount,
  parseVenueAggregateWindows,
  SpotlightBadgeCategory,
  SpotlightCurrentVenueConfig,
  SpotlightEligibilityConfig,
  SpotlightTrendingConfig,
  validateSpotlightBadgePriorityForSave,
  validateSpotlightTrendingForSave,
  VenueActivityType,
  VenueAggregateWindowsConfig,
  VenueGroupingMode,
  MAX_VENUE_AGGREGATE_WINDOW_DAYS,
  MIN_VENUE_AGGREGATE_WINDOW_DAYS,
  HotspotConfig,
  parseHotspotConfig,
  parseVenueTypeLabels,
  validateHotspotConfigForSave,
  validateMapActionsForSave,
  validateMapFilterConfigForSave,
  validateVenueTypeLabelsForSave,
  VenueTypeLabelsConfig,
  HOTSPOT_CONFIG_FIELD_LABELS,
  HOTSPOT_CONFIG_FIELD_DESCRIPTIONS,
  MIN_HOTSPOT_MIN_USERS,
  MAX_HOTSPOT_MIN_USERS,
  MIN_HOTSPOT_RANGE_METERS,
  MAX_HOTSPOT_RANGE_METERS,
  MIN_HOTSPOT_GAP_METERS,
  MAX_HOTSPOT_GAP_METERS,
  MIN_HOTSPOT_ACTIVE_WINDOW_MINUTES,
  MAX_HOTSPOT_ACTIVE_WINDOW_MINUTES,
  MIN_HOTSPOT_CACHE_TTL_SECONDS,
  MAX_HOTSPOT_CACHE_TTL_SECONDS,
  SUPPORT_NAME_MAX_LENGTH,
  MyPlacesProximityConfig,
  MyPlacesProximityRolloutMode,
  parseMyPlacesProximityConfig,
  validateMyPlacesProximityConfigForSave,
  MY_PLACES_PROXIMITY_ROLLOUT_MODE_LABELS,
  MY_PLACES_PROXIMITY_CONFIG_FIELD_LABELS,
  MY_PLACES_PROXIMITY_CONFIG_FIELD_DESCRIPTIONS,
  MIN_MY_PLACES_PROXIMITY_RADIUS_METERS,
  MAX_MY_PLACES_PROXIMITY_RADIUS_METERS,
  MIN_MY_PLACES_PROXIMITY_COOLDOWN_MINUTES,
  MAX_MY_PLACES_PROXIMITY_COOLDOWN_MINUTES,
} from '@ivy-monorepo/shared'

/** Which map users see right now, per the Map UI switch. */
const MapUiStatusTag = ({ live, fallback = false }: { live: boolean; fallback?: boolean }) =>
  live ? (
    <span className="map-ui-status-tag is-live">Live — Map UI {fallback ? 'off' : 'on'}</span>
  ) : (
    <span className="map-ui-status-tag">{fallback ? 'Fallback — used when Map UI is off' : 'Inactive — Map UI is off'}</span>
  )

const FeatureFlagsPage = () => {
  const [flags, setFlags] = useState<FeatureListItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [savingMapFiltersSection, setSavingMapFiltersSection] = useState<'ticker' | 'legacyFilters' | 'windows' | null>(null)
  const [minAppVersionDraft, setMinAppVersionDraft] = useState('')
  const [supportNameDraft, setSupportNameDraft] = useState('')
  const [venueSuggestionAutoReplyDraft, setVenueSuggestionAutoReplyDraft] = useState('')
  const [venueSuggestionAutoReplyEnabledDraft, setVenueSuggestionAutoReplyEnabledDraft] = useState(true)
  const [feedbackAutoReplyDraft, setFeedbackAutoReplyDraft] = useState('')
  const [feedbackAutoReplyEnabledDraft, setFeedbackAutoReplyEnabledDraft] = useState(false)
  // Old-map filter chips (/me mapFilters) — what the app shows while Map UI is off.
  const [mapFiltersDraft, setMapFiltersDraft] = useState<MapFilterConfigItemDto[]>(() => buildDefaultMapFilterConfig())
  const [mapActionsDraft, setMapActionsDraft] = useState<MapActionsConfig>(() => parseMapActions(undefined))
  const [spotlightVenueCountDraft, setSpotlightVenueCountDraft] = useState(DEFAULT_SPOTLIGHT_VENUE_COUNT)
  const [spotlightSortOrderDraft, setSpotlightSortOrderDraft] = useState<VenueActivityType[]>(() =>
    parseSpotlightSortOrder(undefined),
  )
  const [spotlightEligibilityDraft, setSpotlightEligibilityDraft] = useState<SpotlightEligibilityConfig>(() =>
    parseSpotlightEligibility(undefined),
  )
  const [spotlightCurrentVenueDraft, setSpotlightCurrentVenueDraft] = useState<SpotlightCurrentVenueConfig>(() =>
    parseSpotlightCurrentVenue(undefined),
  )
  const [spotlightTrendingDraft, setSpotlightTrendingDraft] = useState<SpotlightTrendingConfig>(() =>
    parseSpotlightTrending(undefined),
  )
  const [spotlightBadgePriorityDraft, setSpotlightBadgePriorityDraft] = useState<SpotlightBadgeCategory[]>(() =>
    parseSpotlightBadgePriority(undefined),
  )
  const [venueAggregateWindowsDraft, setVenueAggregateWindowsDraft] = useState<VenueAggregateWindowsConfig>(() =>
    parseVenueAggregateWindows(undefined),
  )
  const [hotspotConfigDraft, setHotspotConfigDraft] = useState<HotspotConfig>(() => parseHotspotConfig(undefined))
  const [venueTypeLabelsDraft, setVenueTypeLabelsDraft] = useState<VenueTypeLabelsConfig>(() =>
    parseVenueTypeLabels(undefined),
  )
  const [myPlacesProximityDraft, setMyPlacesProximityDraft] = useState<MyPlacesProximityConfig>(() =>
    parseMyPlacesProximityConfig(undefined),
  )

  useEffect(() => {
    const run = async () => {
      try {
        const res = await adminService.getFeatures()
        setFlags(res)

        const minApp = res.find(f => f.key === FeatureFlagKey.MinimumAppVersion)?.payload?.value
        if (typeof minApp === 'string') {
          setMinAppVersionDraft(minApp)
        }

        const supportName = res.find(f => f.key === FeatureFlagKey.SupportName)?.payload?.value
        setSupportNameDraft(typeof supportName === 'string' ? supportName : DEFAULT_SUPPORT_NAME)

        const venueSuggestionAutoReply = res.find(
          f => f.key === FeatureFlagKey.VenueSuggestionAutoReplyMessage,
        )?.payload?.value
        setVenueSuggestionAutoReplyDraft(
          typeof venueSuggestionAutoReply === 'string' ? venueSuggestionAutoReply : DEFAULT_VENUE_SUGGESTION_AUTO_REPLY_MESSAGE,
        )
        const venueSuggestionAutoReplyEnabled = res.find(
          f => f.key === FeatureFlagKey.VenueSuggestionAutoReplyEnabled,
        )?.payload?.value
        setVenueSuggestionAutoReplyEnabledDraft(
          typeof venueSuggestionAutoReplyEnabled === 'boolean' ? venueSuggestionAutoReplyEnabled : true,
        )

        const feedbackAutoReply = res.find(f => f.key === FeatureFlagKey.FeedbackAutoReplyMessage)?.payload?.value
        setFeedbackAutoReplyDraft(
          typeof feedbackAutoReply === 'string' ? feedbackAutoReply : DEFAULT_FEEDBACK_AUTO_REPLY_MESSAGE,
        )
        const feedbackAutoReplyEnabled = res.find(f => f.key === FeatureFlagKey.FeedbackAutoReplyEnabled)?.payload?.value
        setFeedbackAutoReplyEnabledDraft(typeof feedbackAutoReplyEnabled === 'boolean' ? feedbackAutoReplyEnabled : false)

        const mapFiltersFlag = res.find(f => f.key === FeatureFlagKey.MapFilters)
        setMapFiltersDraft(normalizeMapFilterConfig(mapFiltersFlag?.payload?.value))
        setSpotlightVenueCountDraft(parseSpotlightVenueCount(mapFiltersFlag?.payload?.spotlightVenueCount))
        setSpotlightSortOrderDraft(parseSpotlightSortOrder(mapFiltersFlag?.payload?.spotlightSortOrder))
        setSpotlightEligibilityDraft(parseSpotlightEligibility(mapFiltersFlag?.payload?.spotlightEligibility))
        setSpotlightCurrentVenueDraft(parseSpotlightCurrentVenue(mapFiltersFlag?.payload?.spotlightCurrentVenue))
        setSpotlightTrendingDraft(parseSpotlightTrending(mapFiltersFlag?.payload?.spotlightTrending))
        setSpotlightBadgePriorityDraft(parseSpotlightBadgePriority(mapFiltersFlag?.payload?.spotlightBadgePriority))
        setVenueAggregateWindowsDraft(parseVenueAggregateWindows(mapFiltersFlag?.payload?.venueAggregateWindows))

        const hotspotConfigFlag = res.find(f => f.key === FeatureFlagKey.HotspotConfig)
        setHotspotConfigDraft(parseHotspotConfig(hotspotConfigFlag?.payload?.value))

        const mapActionsFlag = res.find(f => f.key === FeatureFlagKey.MapActions)
        setMapActionsDraft(parseMapActions(mapActionsFlag?.payload?.value))

        const venueTypeLabelsFlag = res.find(f => f.key === FeatureFlagKey.VenueTypeLabels)
        setVenueTypeLabelsDraft(parseVenueTypeLabels(venueTypeLabelsFlag?.payload?.value))

        const myPlacesProximityFlag = res.find(f => f.key === FeatureFlagKey.MyPlacesProximity)
        setMyPlacesProximityDraft(parseMyPlacesProximityConfig(myPlacesProximityFlag?.payload?.value))
      } finally {
        setLoading(false)
      }
    }
    run()
  }, [])

  const toggleNewMap = async () => {
    const key = FeatureFlagKey.MapVersion
    const existing = flags.find(f => f.key === key)
    const current = existing?.payload?.value === MapVersion.New ? MapVersion.New : MapVersion.Legacy
    const next = current === MapVersion.New ? MapVersion.Legacy : MapVersion.New
    setSaving(key)
    try {
      const updated = await adminService.setFeatureValue(key, next)
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, ...updated } : f)))
    } finally {
      setSaving(null)
    }
  }

  const toggleVenueGrouping = async () => {
    const key = FeatureFlagKey.VenueGroupingMode
    const existing = flags.find(f => f.key === key)
    const current =
      existing?.payload?.value === VenueGroupingMode.Grouped ? VenueGroupingMode.Grouped : VenueGroupingMode.Ungrouped
    const next = current === VenueGroupingMode.Grouped ? VenueGroupingMode.Ungrouped : VenueGroupingMode.Grouped
    setSaving(key)
    try {
      const updated = await adminService.setFeatureValue(key, next)
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, ...updated } : f)))
    } finally {
      setSaving(null)
    }
  }

  const saveMinimumAppVersion = async () => {
    const key = FeatureFlagKey.MinimumAppVersion
    const trimmed = minAppVersionDraft.trim()
    if (!isValidSemverXyz(trimmed)) {
      return
    }
    setSaving(key)
    try {
      const updated = await adminService.setFeatureValue(key, trimmed)
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, ...updated } : f)))
    } finally {
      setSaving(null)
    }
  }

  const supportNameValid = supportNameDraft.trim().length > 0 && supportNameDraft.trim().length <= SUPPORT_NAME_MAX_LENGTH

  const saveSupportName = async () => {
    const key = FeatureFlagKey.SupportName
    const trimmed = supportNameDraft.trim()
    if (!trimmed || trimmed.length > SUPPORT_NAME_MAX_LENGTH) {
      return
    }
    setSaving(key)
    try {
      const updated = await adminService.setFeatureValue(key, trimmed)
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, ...updated } : f)))
      setSupportNameDraft(typeof updated.payload?.value === 'string' ? updated.payload.value : trimmed)
    } finally {
      setSaving(null)
    }
  }

  const venueSuggestionAutoReplyValid = venueSuggestionAutoReplyDraft.trim().length <= VENUE_SUGGESTION_AUTO_REPLY_MAX_LENGTH

  const saveVenueSuggestionAutoReply = async () => {
    const messageKey = FeatureFlagKey.VenueSuggestionAutoReplyMessage
    const trimmed = venueSuggestionAutoReplyDraft.trim()
    if (trimmed.length > VENUE_SUGGESTION_AUTO_REPLY_MAX_LENGTH) {
      return
    }
    setSaving(messageKey)
    try {
      const updatedMessage = await adminService.setFeatureValue(messageKey, trimmed)
      const updatedEnabled = await adminService.setFeatureValue(
        FeatureFlagKey.VenueSuggestionAutoReplyEnabled,
        venueSuggestionAutoReplyEnabledDraft,
      )
      setFlags(prev =>
        prev
          .map(f => (f.key === messageKey ? { ...f, ...updatedMessage } : f))
          .map(f => (f.key === FeatureFlagKey.VenueSuggestionAutoReplyEnabled ? { ...f, ...updatedEnabled } : f)),
      )
      setVenueSuggestionAutoReplyDraft(
        typeof updatedMessage.payload?.value === 'string' ? updatedMessage.payload.value : trimmed,
      )
    } finally {
      setSaving(null)
    }
  }

  const feedbackAutoReplyValid = feedbackAutoReplyDraft.trim().length <= FEEDBACK_AUTO_REPLY_MAX_LENGTH

  const saveFeedbackAutoReply = async () => {
    const messageKey = FeatureFlagKey.FeedbackAutoReplyMessage
    const trimmed = feedbackAutoReplyDraft.trim()
    if (trimmed.length > FEEDBACK_AUTO_REPLY_MAX_LENGTH) {
      return
    }
    setSaving(messageKey)
    try {
      const updatedMessage = await adminService.setFeatureValue(messageKey, trimmed)
      const updatedEnabled = await adminService.setFeatureValue(
        FeatureFlagKey.FeedbackAutoReplyEnabled,
        feedbackAutoReplyEnabledDraft,
      )
      setFlags(prev =>
        prev
          .map(f => (f.key === messageKey ? { ...f, ...updatedMessage } : f))
          .map(f => (f.key === FeatureFlagKey.FeedbackAutoReplyEnabled ? { ...f, ...updatedEnabled } : f)),
      )
      setFeedbackAutoReplyDraft(typeof updatedMessage.payload?.value === 'string' ? updatedMessage.payload.value : trimmed)
    } finally {
      setSaving(null)
    }
  }

  const mapVersionFlag = flags.find(f => f.key === FeatureFlagKey.MapVersion)
  const mapVersion: MapVersion = mapVersionFlag?.payload?.value === MapVersion.New ? MapVersion.New : MapVersion.Legacy
  const newMapEnabled = mapVersion === MapVersion.New

  const venueGroupingFlag = flags.find(f => f.key === FeatureFlagKey.VenueGroupingMode)
  const venueGroupingMode: VenueGroupingMode =
    venueGroupingFlag?.payload?.value === VenueGroupingMode.Grouped
      ? VenueGroupingMode.Grouped
      : VenueGroupingMode.Ungrouped
  const venuesGrouped = venueGroupingMode === VenueGroupingMode.Grouped

  const spotlightVenueCountValid =
    Number.isInteger(spotlightVenueCountDraft)
    && spotlightVenueCountDraft >= MIN_SPOTLIGHT_VENUE_COUNT
    && spotlightVenueCountDraft <= MAX_SPOTLIGHT_VENUE_COUNT

  const spotlightEligibilityValid =
    Number.isFinite(spotlightEligibilityDraft.searchRadiusDegrees)
    && spotlightEligibilityDraft.searchRadiusDegrees >= MIN_SPOTLIGHT_SEARCH_RADIUS_DEGREES
    && spotlightEligibilityDraft.searchRadiusDegrees <= MAX_SPOTLIGHT_SEARCH_RADIUS_DEGREES
    && Number.isInteger(spotlightEligibilityDraft.recentViewsWindowDays)
    && spotlightEligibilityDraft.recentViewsWindowDays >= MIN_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS
    && spotlightEligibilityDraft.recentViewsWindowDays <= MAX_SPOTLIGHT_RECENT_VIEWS_WINDOW_DAYS
    && hasEnabledSpotlightEligibilityRule(spotlightEligibilityDraft.rules)

  const spotlightTrendingValid = validateSpotlightTrendingForSave(spotlightTrendingDraft) === null
  const spotlightBadgePriorityValid = validateSpotlightBadgePriorityForSave(spotlightBadgePriorityDraft) === null

  const tickerSettingsValid =
    spotlightVenueCountValid && spotlightEligibilityValid && spotlightTrendingValid && spotlightBadgePriorityValid

  const venueAggregateWindowsValid =
    Number.isInteger(venueAggregateWindowsDraft.recentCheckinsWindowDays)
    && venueAggregateWindowsDraft.recentCheckinsWindowDays >= MIN_VENUE_AGGREGATE_WINDOW_DAYS
    && venueAggregateWindowsDraft.recentCheckinsWindowDays <= MAX_VENUE_AGGREGATE_WINDOW_DAYS
    && Number.isInteger(venueAggregateWindowsDraft.recentViewsWindowDays)
    && venueAggregateWindowsDraft.recentViewsWindowDays >= MIN_VENUE_AGGREGATE_WINDOW_DAYS
    && venueAggregateWindowsDraft.recentViewsWindowDays <= MAX_VENUE_AGGREGATE_WINDOW_DAYS

  const hotspotConfigValid = validateHotspotConfigForSave(hotspotConfigDraft) === null

  const saveHotspotConfig = async () => {
    if (!hotspotConfigValid) {
      return
    }
    const key = FeatureFlagKey.HotspotConfig
    setSaving(key)
    try {
      const updated = await adminService.setFeatureValue(key, hotspotConfigDraft)
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, ...updated } : f)))
      setHotspotConfigDraft(parseHotspotConfig(updated.payload?.value))
    } finally {
      setSaving(null)
    }
  }

  // Map UI switch (top card): on = new map (mapActions), off = old map (mapFilters).
  const newMapUiLive = flags.find(f => f.key === FeatureFlagKey.MapUiEnabled)?.payload?.value === true

  const updateMapFilterDraft = (key: MapFilterConfigItemDto['key'], patch: Partial<MapFilterConfigItemDto>) => {
    setMapFiltersDraft(prev => prev.map(item => (item.key === key ? { ...item, ...patch } : item)))
  }
  const legacyMapFiltersError = validateMapFilterConfigForSave(mapFiltersDraft)
  const legacyMapFiltersDirty =
    JSON.stringify(normalizeMapFilterConfig(flags.find(f => f.key === FeatureFlagKey.MapFilters)?.payload?.value))
    !== JSON.stringify(normalizeMapFilterConfig(mapFiltersDraft))

  const mapActionsError = validateMapActionsForSave(mapActionsDraft)
  const savedMapActions = parseMapActions(flags.find(f => f.key === FeatureFlagKey.MapActions)?.payload?.value)
  const mapActionsDirty = JSON.stringify(savedMapActions) !== JSON.stringify(mapActionsDraft)

  const saveMapActions = async () => {
    if (mapActionsError) {
      return
    }
    const key = FeatureFlagKey.MapActions
    setSaving(key)
    try {
      const updated = await adminService.setFeatureValue(key, mapActionsDraft)
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, ...updated } : f)))
      setMapActionsDraft(parseMapActions(updated.payload?.value))
    } finally {
      setSaving(null)
    }
  }

  const venueTypeLabelsValid = validateVenueTypeLabelsForSave(venueTypeLabelsDraft) === null

  const saveVenueTypeLabels = async () => {
    if (!venueTypeLabelsValid) {
      return
    }
    const key = FeatureFlagKey.VenueTypeLabels
    setSaving(key)
    try {
      const updated = await adminService.setFeatureValue(key, venueTypeLabelsDraft)
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, ...updated } : f)))
      setVenueTypeLabelsDraft(parseVenueTypeLabels(updated.payload?.value))
    } finally {
      setSaving(null)
    }
  }

  const myPlacesProximityValid = validateMyPlacesProximityConfigForSave(myPlacesProximityDraft) === null

  const saveMyPlacesProximityConfig = async () => {
    if (!myPlacesProximityValid) {
      return
    }
    const key = FeatureFlagKey.MyPlacesProximity
    setSaving(key)
    try {
      const updated = await adminService.setFeatureValue(key, myPlacesProximityDraft)
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, ...updated } : f)))
      setMyPlacesProximityDraft(parseMyPlacesProximityConfig(updated.payload?.value))
    } finally {
      setSaving(null)
    }
  }

  // The ticker, old-map filters and activity windows share the mapFilters flag;
  // each card sends only its own drafts and the saved values for the rest.
  const saveMapFiltersSettings = async (section: 'ticker' | 'legacyFilters' | 'windows') => {
    const key = FeatureFlagKey.MapFilters
    if (section === 'ticker' && !tickerSettingsValid) {
      return
    }
    if (section === 'legacyFilters' && legacyMapFiltersError) {
      return
    }
    if (section === 'windows' && !venueAggregateWindowsValid) {
      return
    }

    const savedMapFiltersFlag = flags.find(f => f.key === key)
    const filtersForSave =
      section === 'legacyFilters' ? mapFiltersDraft : normalizeMapFilterConfig(savedMapFiltersFlag?.payload?.value)
    const spotlightVenueCountForSave = section === 'ticker' && spotlightVenueCountValid
      ? spotlightVenueCountDraft
      : parseSpotlightVenueCount(savedMapFiltersFlag?.payload?.spotlightVenueCount)
    const spotlightSortOrderForSave =
      section === 'ticker'
        ? spotlightSortOrderDraft
        : parseSpotlightSortOrder(savedMapFiltersFlag?.payload?.spotlightSortOrder)
    const spotlightEligibilityForSave = section === 'ticker' && spotlightEligibilityValid
      ? spotlightEligibilityDraft
      : parseSpotlightEligibility(savedMapFiltersFlag?.payload?.spotlightEligibility)
    const spotlightCurrentVenueForSave =
      section === 'ticker'
        ? spotlightCurrentVenueDraft
        : parseSpotlightCurrentVenue(savedMapFiltersFlag?.payload?.spotlightCurrentVenue)
    const spotlightTrendingForSave =
      section === 'ticker' && spotlightTrendingValid
        ? spotlightTrendingDraft
        : parseSpotlightTrending(savedMapFiltersFlag?.payload?.spotlightTrending)
    const spotlightBadgePriorityForSave =
      section === 'ticker' && spotlightBadgePriorityValid
        ? spotlightBadgePriorityDraft
        : parseSpotlightBadgePriority(savedMapFiltersFlag?.payload?.spotlightBadgePriority)
    const venueAggregateWindowsForSave =
      section === 'windows' && venueAggregateWindowsValid
        ? venueAggregateWindowsDraft
        : parseVenueAggregateWindows(savedMapFiltersFlag?.payload?.venueAggregateWindows)

    setSaving(key)
    setSavingMapFiltersSection(section)
    try {
      const updated = await adminService.setFeatureValue(key, {
        filters: filtersForSave,
        spotlightVenueCount: spotlightVenueCountForSave,
        spotlightSortOrder: spotlightSortOrderForSave,
        spotlightEligibility: spotlightEligibilityForSave,
        spotlightCurrentVenue: spotlightCurrentVenueForSave,
        spotlightTrending: spotlightTrendingForSave,
        spotlightBadgePriority: spotlightBadgePriorityForSave,
        venueAggregateWindows: venueAggregateWindowsForSave,
      })
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, ...updated } : f)))
      // Refresh only the saved card, so other cards keep their unsaved edits.
      if (section === 'legacyFilters') {
        setMapFiltersDraft(normalizeMapFilterConfig(updated.payload?.value))
      }
      if (section === 'ticker') {
        setSpotlightVenueCountDraft(parseSpotlightVenueCount(updated.payload?.spotlightVenueCount))
        setSpotlightSortOrderDraft(parseSpotlightSortOrder(updated.payload?.spotlightSortOrder))
        setSpotlightEligibilityDraft(parseSpotlightEligibility(updated.payload?.spotlightEligibility))
        setSpotlightCurrentVenueDraft(parseSpotlightCurrentVenue(updated.payload?.spotlightCurrentVenue))
        setSpotlightTrendingDraft(parseSpotlightTrending(updated.payload?.spotlightTrending))
        setSpotlightBadgePriorityDraft(parseSpotlightBadgePriority(updated.payload?.spotlightBadgePriority))
      }
      if (section === 'windows') {
        setVenueAggregateWindowsDraft(parseVenueAggregateWindows(updated.payload?.venueAggregateWindows))
      }
    } finally {
      setSaving(null)
      setSavingMapFiltersSection(null)
    }
  }

  return (
    <div className="page feature-flags-page">
      <div className="page-header">
        <h1>Feature Flags</h1>
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <AppFeatureTogglesCard
            flags={flags}
            onUpdated={updated => setFlags(prev => prev.map(f => (f.key === updated.key ? { ...f, ...updated } : f)))}
          />

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row">
              <div>
                <div className="feature-flag-title">Map experience</div>
                <div className="feature-flag-subtitle">
                  Controls which map users see: <strong>{newMapEnabled ? 'New Map' : 'Legacy Map'}</strong>
                </div>
              </div>
              <div className="feature-flag-actions">
                <button className="styled-button sm" onClick={toggleNewMap} disabled={saving === FeatureFlagKey.MapVersion}>
                  {saving === FeatureFlagKey.MapVersion ? 'Saving…' : newMapEnabled ? 'Switch to Legacy' : 'Switch to New'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row">
              <div>
                <div className="feature-flag-title">Venue grouping (mobile map)</div>
                <div className="feature-flag-subtitle">
                  Sent to clients as <code>/me</code> <code>venueGroupingMode</code>. Current:{' '}
                  <strong>{venuesGrouped ? 'Grouped' : 'Ungrouped'}</strong>
                </div>
              </div>
              <div className="feature-flag-actions">
                <button
                  className="styled-button sm"
                  onClick={toggleVenueGrouping}
                  disabled={saving === FeatureFlagKey.VenueGroupingMode}
                >
                  {saving === FeatureFlagKey.VenueGroupingMode
                    ? 'Saving…'
                    : venuesGrouped
                      ? 'Switch to Ungrouped'
                      : 'Switch to Grouped'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">Spotlight ticker (mobile)</div>
                <SpotlightTickerGuide />
                <label className="ticker-setting-field">
                  <span className="ticker-setting-label">Venues returned</span>
                  <input
                    type="number"
                    className="map-filter-input ticker-setting-count-input"
                    min={MIN_SPOTLIGHT_VENUE_COUNT}
                    max={MAX_SPOTLIGHT_VENUE_COUNT}
                    step={1}
                    value={spotlightVenueCountDraft}
                    onChange={e => setSpotlightVenueCountDraft(Number.parseInt(e.target.value, 10) || 0)}
                  />
                  <span className="feature-flag-subtitle">
                    How many ranked venue cards the API returns after sorting (1–{MAX_SPOTLIGHT_VENUE_COUNT}, default{' '}
                    {DEFAULT_SPOTLIGHT_VENUE_COUNT}). The server may evaluate up to {MAX_SPOTLIGHT_VENUE_CANDIDATES}{' '}
                    candidates before slicing to this limit.
                  </span>
                </label>
                {!spotlightVenueCountValid ? (
                  <div className="feature-flag-subtitle ticker-setting-error">
                    Enter an integer from {MIN_SPOTLIGHT_VENUE_COUNT} to {MAX_SPOTLIGHT_VENUE_COUNT}.
                  </div>
                ) : null}
                <div className="ticker-setting-block">
                  <div className="ticker-setting-label">Eligibility rules</div>
                  <SpotlightEligibilityEditor
                    value={spotlightEligibilityDraft}
                    onChange={setSpotlightEligibilityDraft}
                  />
                </div>
                <div className="ticker-setting-block">
                  <div className="ticker-setting-label">Current check-in</div>
                  <SpotlightCurrentVenueSettings
                    value={spotlightCurrentVenueDraft}
                    onChange={setSpotlightCurrentVenueDraft}
                  />
                </div>
                <div className="ticker-setting-block">
                  <div className="ticker-setting-label">Trending</div>
                  <SpotlightTrendingSettings
                    value={spotlightTrendingDraft}
                    onChange={setSpotlightTrendingDraft}
                  />
                </div>
                <div className="ticker-setting-block">
                  <div className="ticker-setting-label">Badge priority</div>
                  <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                    A venue can match several categories at once (e.g. Sponsored <strong>and</strong> Trending). Drag
                    to set which single badge/subtitle the ticker card shows — first match wins. This does not affect
                    which venues appear, only which badge a matching venue gets.
                  </div>
                  <SpotlightBadgePriorityTable
                    order={spotlightBadgePriorityDraft}
                    onChange={setSpotlightBadgePriorityDraft}
                  />
                </div>
                <div className="ticker-setting-block">
                  <div className="ticker-setting-label">Ranking priority</div>
                  <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                    Drag rows to set which activity signal wins when two venues are compared. Position 0 is tried first;
                    if it does not separate them, the next row is used. The <code>events</code> slot covers event RSVPs{' '}
                    <strong>or</strong> visit plans for today at the same priority. After this list, the server always
                    applies <em>recent viewers</em> then <em>distance</em>. When pin-first is enabled above, the
                    user&apos;s current check-in is sorted ahead of this entire list.
                  </div>
                  <SpotlightSortOrderTable order={spotlightSortOrderDraft} onChange={setSpotlightSortOrderDraft} />
                </div>
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={() => saveMapFiltersSettings('ticker')}
                  disabled={savingMapFiltersSection !== null || !tickerSettingsValid}
                >
                  {savingMapFiltersSection === 'ticker' ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">
                  Map filters (mobile) — new map
                  <MapUiStatusTag live={newMapUiLive} />
                  {mapActionsDirty ? <span className="feature-flag-unsaved">Unsaved changes</span> : null}
                </div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  The filter sidebar on the new map. Picking a filter narrows the venue markers to what it covers and
                  switches their badges to its color. Drag rows to reorder (top of the list = top of the sidebar),
                  switch availability inline, or <strong>Edit</strong> a filter to change its icon, name, pill text and
                  color. Sent to clients as <code>/me</code> <code>mapActions</code> and live over the socket; the app
                  keeps the last config, so changes ship without a release.
                </div>
                <MapActionsEditor value={mapActionsDraft} onChange={setMapActionsDraft} />
                {mapActionsError ? (
                  <div className="feature-flag-subtitle ticker-setting-error map-actions-error">{mapActionsError.message}</div>
                ) : null}
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={saveMapActions}
                  disabled={saving === FeatureFlagKey.MapActions || mapActionsError !== null || !mapActionsDirty}
                >
                  {saving === FeatureFlagKey.MapActions ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">
                  Map filters (mobile) — old map
                  <MapUiStatusTag live={!newMapUiLive} fallback />
                  {legacyMapFiltersDirty ? <span className="feature-flag-unsaved">Unsaved changes</span> : null}
                </div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  The filter chips of the old map, which the app shows whenever <strong>Map UI</strong> is switched off
                  — keep them in shape so switching back is safe. Sent to clients as <code>/me</code>{' '}
                  <code>mapFilters</code>; keys match venue activity stats. <strong>Locked</strong> = always on;{' '}
                  <strong>Init</strong> = on at first launch; <strong>Default</strong> = off until enabled;{' '}
                  <strong>Hidden</strong> = not shown in the filter bar. Row order also sets the highlight aggregates
                  (peek / venue detail) on the server. Drag rows to reorder.
                </div>
                <MapFiltersTable
                  items={mapFiltersDraft}
                  onChange={setMapFiltersDraft}
                  onUpdateItem={updateMapFilterDraft}
                />
                {legacyMapFiltersError ? (
                  <div className="feature-flag-subtitle ticker-setting-error map-actions-error">
                    {legacyMapFiltersError.message}
                  </div>
                ) : null}
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={() => saveMapFiltersSettings('legacyFilters')}
                  disabled={savingMapFiltersSection !== null || legacyMapFiltersError !== null || !legacyMapFiltersDirty}
                >
                  {savingMapFiltersSection === 'legacyFilters' ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">Activity stat windows</div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  How many days back count toward <code>recentCheckins</code> and <code>views</code> on the map, in
                  the spotlight ticker and in venue aggregates. Server-only; not sent in <code>/me</code>.
                </div>
                <VenueAggregateWindowsEditor
                  value={venueAggregateWindowsDraft}
                  onChange={setVenueAggregateWindowsDraft}
                />
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={() => saveMapFiltersSettings('windows')}
                  disabled={savingMapFiltersSection !== null || !venueAggregateWindowsValid}
                >
                  {savingMapFiltersSection === 'windows' ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">Support name</div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  Name shown to users as the sender of feedback replies and admin warning notifications, in place of
                  the real admin's name.
                </div>
                <TextInput
                  type="text"
                  label="Support name"
                  name="supportName"
                  required={false}
                  value={supportNameDraft}
                  onChange={(value: string) => setSupportNameDraft(value)}
                />
                {!supportNameValid ? (
                  <div className="feature-flag-subtitle" style={{ color: 'var(--error-color, #c00)', marginTop: 8 }}>
                    Enter 1–{SUPPORT_NAME_MAX_LENGTH} characters.
                  </div>
                ) : null}
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={saveSupportName}
                  disabled={saving === FeatureFlagKey.SupportName || !supportNameValid}
                >
                  {saving === FeatureFlagKey.SupportName ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">Venue suggestion auto-reply</div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  Posted automatically, as the first admin reply, when a user suggests a venue from the map — opens
                  their feedback thread already acknowledged.
                </div>
                <label className="feature-flag-checkbox-label">
                  <input
                    type="checkbox"
                    checked={venueSuggestionAutoReplyEnabledDraft}
                    onChange={event => setVenueSuggestionAutoReplyEnabledDraft(event.target.checked)}
                  />
                  Enabled
                </label>
                <textarea
                  className="feature-flag-textarea"
                  value={venueSuggestionAutoReplyDraft}
                  onChange={event => setVenueSuggestionAutoReplyDraft(event.target.value)}
                  rows={3}
                  placeholder={DEFAULT_VENUE_SUGGESTION_AUTO_REPLY_MESSAGE}
                  disabled={!venueSuggestionAutoReplyEnabledDraft}
                />
                {!venueSuggestionAutoReplyValid ? (
                  <div className="feature-flag-subtitle" style={{ color: 'var(--error-color, #c00)', marginTop: 8 }}>
                    Must be at most {VENUE_SUGGESTION_AUTO_REPLY_MAX_LENGTH} characters.
                  </div>
                ) : null}
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={saveVenueSuggestionAutoReply}
                  disabled={saving === FeatureFlagKey.VenueSuggestionAutoReplyMessage || !venueSuggestionAutoReplyValid}
                >
                  {saving === FeatureFlagKey.VenueSuggestionAutoReplyMessage ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">General feedback auto-reply</div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  Posted automatically, as the first admin reply, on any other new feedback topic (not a venue
                  suggestion). Off by default.
                </div>
                <label className="feature-flag-checkbox-label">
                  <input
                    type="checkbox"
                    checked={feedbackAutoReplyEnabledDraft}
                    onChange={event => setFeedbackAutoReplyEnabledDraft(event.target.checked)}
                  />
                  Enabled
                </label>
                <textarea
                  className="feature-flag-textarea"
                  value={feedbackAutoReplyDraft}
                  onChange={event => setFeedbackAutoReplyDraft(event.target.value)}
                  rows={3}
                  placeholder={DEFAULT_FEEDBACK_AUTO_REPLY_MESSAGE}
                  disabled={!feedbackAutoReplyEnabledDraft}
                />
                {!feedbackAutoReplyValid ? (
                  <div className="feature-flag-subtitle" style={{ color: 'var(--error-color, #c00)', marginTop: 8 }}>
                    Must be at most {FEEDBACK_AUTO_REPLY_MAX_LENGTH} characters.
                  </div>
                ) : null}
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={saveFeedbackAutoReply}
                  disabled={saving === FeatureFlagKey.FeedbackAutoReplyMessage || !feedbackAutoReplyValid}
                >
                  {saving === FeatureFlagKey.FeedbackAutoReplyMessage ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">Minimum / published app version</div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  Semver you set here is sent to clients as <code>/me</code> <code>appVersion</code> and as{' '}
                  <code>publishedAppVersion</code> on user payloads. If it is missing or invalid,{' '}
                  <code>publishedAppVersion</code> falls back to the <code>PUBLISHED_APP_VERSION</code> environment variable.
                </div>
                <TextInput
                  type="text"
                  label="X.Y.Z"
                  name="minimumAppVersion"
                  required={false}
                  value={minAppVersionDraft}
                  onChange={(value: string) => setMinAppVersionDraft(value)}
                />
                {minAppVersionDraft.trim().length > 0 && !isValidSemverXyz(minAppVersionDraft.trim()) ? (
                  <div className="feature-flag-subtitle" style={{ color: 'var(--error-color, #c00)', marginTop: 8 }}>
                    Use three non-negative numbers separated by dots (for example 2.1.0).
                  </div>
                ) : null}
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={saveMinimumAppVersion}
                  disabled={
                    saving === FeatureFlagKey.MinimumAppVersion
                    || !isValidSemverXyz(minAppVersionDraft.trim())
                  }
                >
                  {saving === FeatureFlagKey.MinimumAppVersion ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">Hotspots</div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  Powers <code>GET /user/venues/hotspots</code>. A hotspot is a cluster of at least{' '}
                  <strong>Minimum users</strong> (real + simulated) within <strong>Detection range</strong> of each
                  other. Two hotspots closer than <strong>Minimum gap</strong> are merged into one larger hotspot.
                  Results are cached for <strong>Cache TTL</strong>.
                </div>
                <div className="ticker-settings-grid">
                  {(
                    [
                      ['minUsersForHotspot', MIN_HOTSPOT_MIN_USERS, MAX_HOTSPOT_MIN_USERS],
                      ['rangeMeters', MIN_HOTSPOT_RANGE_METERS, MAX_HOTSPOT_RANGE_METERS],
                      ['minGapMeters', MIN_HOTSPOT_GAP_METERS, MAX_HOTSPOT_GAP_METERS],
                      ['activeWindowMinutes', MIN_HOTSPOT_ACTIVE_WINDOW_MINUTES, MAX_HOTSPOT_ACTIVE_WINDOW_MINUTES],
                      ['cacheTtlSeconds', MIN_HOTSPOT_CACHE_TTL_SECONDS, MAX_HOTSPOT_CACHE_TTL_SECONDS],
                    ] as const
                  ).map(([field, min, max]) => (
                    <label className="ticker-setting-field" key={field}>
                      <span className="ticker-setting-label">{HOTSPOT_CONFIG_FIELD_LABELS[field]}</span>
                      <input
                        type="number"
                        className="map-filter-input ticker-setting-count-input"
                        min={min}
                        max={max}
                        step={1}
                        value={hotspotConfigDraft[field]}
                        onChange={e =>
                          setHotspotConfigDraft(prev => ({
                            ...prev,
                            [field]: Number.parseInt(e.target.value, 10) || 0,
                          }))
                        }
                      />
                      <span className="feature-flag-subtitle">
                        {HOTSPOT_CONFIG_FIELD_DESCRIPTIONS[field]} ({min}–{max})
                      </span>
                    </label>
                  ))}
                </div>
                {!hotspotConfigValid ? (
                  <div className="feature-flag-subtitle" style={{ color: 'var(--error-color, #c00)', marginTop: 8 }}>
                    Enter integers within the ranges shown above.
                  </div>
                ) : null}
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={saveHotspotConfig}
                  disabled={saving === FeatureFlagKey.HotspotConfig || !hotspotConfigValid}
                >
                  {saving === FeatureFlagKey.HotspotConfig ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">Venue type labels (mobile)</div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  Powers <code>GET user/venues/types</code> — the map&apos;s category filter chips. One row per{' '}
                  <code>VenueType</code>; label and emoji ship without a mobile release.
                </div>
                <VenueTypeLabelsTable value={venueTypeLabelsDraft} onChange={setVenueTypeLabelsDraft} />
                {!venueTypeLabelsValid ? (
                  <div className="feature-flag-subtitle ticker-setting-error">
                    Every row needs a non-empty label and emoji.
                  </div>
                ) : null}
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={saveVenueTypeLabels}
                  disabled={saving === FeatureFlagKey.VenueTypeLabels || !venueTypeLabelsValid}
                >
                  {saving === FeatureFlagKey.VenueTypeLabels ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="card feature-flag-card-spaced">
            <div className="feature-flag-row feature-flag-row-top">
              <div className="feature-flag-grow">
                <div className="feature-flag-title">My Places proximity notifications</div>
                <div className="feature-flag-subtitle feature-flag-subtitle-spaced">
                  When a user is within <strong>Detection radius</strong> of one of their My Places, the system can
                  suggest (or perform) a check-in — behavior is per-user (see user detail page). This rollout
                  switch controls who the feature runs for at all.
                </div>
                <label className="ticker-setting-field">
                  <span className="ticker-setting-label">Rollout</span>
                  <select
                    className="map-filter-input"
                    value={myPlacesProximityDraft.rolloutMode}
                    onChange={e =>
                      setMyPlacesProximityDraft(prev => ({
                        ...prev,
                        rolloutMode: e.target.value as MyPlacesProximityRolloutMode,
                      }))
                    }
                  >
                    {Object.values(MyPlacesProximityRolloutMode).map(mode => (
                      <option key={mode} value={mode}>
                        {MY_PLACES_PROXIMITY_ROLLOUT_MODE_LABELS[mode]}
                      </option>
                    ))}
                  </select>
                  <span className="feature-flag-subtitle">
                    Disabled = off for everyone. Internal users only = safe to test in production against real
                    venues before a full rollout. All users = live for everyone.
                  </span>
                </label>
                <div className="ticker-settings-grid">
                  {(
                    [
                      ['radiusMeters', MIN_MY_PLACES_PROXIMITY_RADIUS_METERS, MAX_MY_PLACES_PROXIMITY_RADIUS_METERS],
                      ['cooldownMinutes', MIN_MY_PLACES_PROXIMITY_COOLDOWN_MINUTES, MAX_MY_PLACES_PROXIMITY_COOLDOWN_MINUTES],
                    ] as const
                  ).map(([field, min, max]) => (
                    <label className="ticker-setting-field" key={field}>
                      <span className="ticker-setting-label">{MY_PLACES_PROXIMITY_CONFIG_FIELD_LABELS[field]}</span>
                      <input
                        type="number"
                        className="map-filter-input ticker-setting-count-input"
                        min={min}
                        max={max}
                        step={1}
                        value={myPlacesProximityDraft[field]}
                        onChange={e =>
                          setMyPlacesProximityDraft(prev => ({
                            ...prev,
                            [field]: Number.parseInt(e.target.value, 10) || 0,
                          }))
                        }
                      />
                      <span className="feature-flag-subtitle">
                        {MY_PLACES_PROXIMITY_CONFIG_FIELD_DESCRIPTIONS[field]} ({min}–{max})
                      </span>
                    </label>
                  ))}
                </div>
                {!myPlacesProximityValid ? (
                  <div className="feature-flag-subtitle" style={{ color: 'var(--error-color, #c00)', marginTop: 8 }}>
                    Enter integers within the ranges shown above.
                  </div>
                ) : null}
              </div>
              <div className="feature-flag-save-sticky">
                <button
                  className="styled-button sm"
                  onClick={saveMyPlacesProximityConfig}
                  disabled={saving === FeatureFlagKey.MyPlacesProximity || !myPlacesProximityValid}
                >
                  {saving === FeatureFlagKey.MyPlacesProximity ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

        </>
      )}
    </div>
  )
}

export default FeatureFlagsPage

